/**
 * Fixes same-page "#shopify-section-<key>" links placed inside page-builder
 * content blocks (e.g. a button or image block with a fragment link).
 *
 * Root cause #1 (scroll container): on desktop (>=990px) this theme's
 * `.page-wrapper` is the real scroll container, while
 * `document.scrollingElement`/`html` is forced `overflow: hidden` (see
 * assets/scroll-container.js, which documents this exact tradeoff and works
 * around it for its own scroll-restoration logic). Because of that, the
 * browser's default fragment-navigation ("jump to #id") has nothing to
 * scroll and silently does nothing.
 *
 * Root cause #2 (dynamic section IDs): Shopify JSON-template sections don't
 * render with the bare `shopify-section-<key>` id used in this theme's page
 * builder `link` settings. At render time Shopify prefixes it with the
 * template instance, e.g. `shopify-section-template--<instance_id>__<key>`.
 * A `document.getElementById('shopify-section-<key>')` lookup against the
 * literal href therefore returns null even though the section exists on the
 * page under a differently-prefixed id. This prefix is generated by Shopify
 * and can change (e.g. if the template/page is duplicated or reassigned),
 * so it can't be hardcoded into the button hrefs — the lookup has to
 * tolerate it instead.
 *
 * Root cause #3 (event-order race): something else in this theme also
 * listens for clicks on these same anchors (in the bubble phase, ahead of a
 * normal bubble-phase listener here) and was winning the race, hijacking
 * the click into an unrelated navigation before this handler's
 * preventDefault() had any effect. Registering this listener on the capture
 * phase, and calling stopImmediatePropagation() as soon as we've confirmed
 * we're handling a same-page hash click, lets this handler run first and
 * stops the competing listener from seeing the event at all.
 *
 * This listens for clicks on same-page hash links, resolves the actual
 * target element (exact id match first, falling back to a suffix match on
 * the "__<key>" or "shopify-section-<key>" part for the dynamic-prefix
 * case), and scrolls it into view directly via `Element.scrollIntoView()`.
 * Calling scrollIntoView on the target (rather than relying on native
 * fragment-navigation) correctly walks up to the actual scrollable ancestor
 * (`.page-wrapper` on desktop, the document on mobile) and respects the
 * `scroll-margin-top` set on `[id^="shopify-section-"]` in
 * assets/anchor-section-scroll.css, so the target also clears the sticky
 * header.
 */
document.addEventListener(
  'click',
  (event) => {
    const anchor = event.target.closest('a[href^="#"]');
    if (!anchor) return;

    const hash = anchor.getAttribute('href');
    if (!hash || hash.length < 2) return;

    const targetId = hash.slice(1);

    // Try an exact id match first (covers ids that aren't Shopify-section
    // ids at all, e.g. hand-authored anchors).
    let target = document.getElementById(targetId);

    if (!target) {
      // Fall back to a suffix match for Shopify's dynamic
      // "shopify-section-template--<instance_id>__<key>" id pattern: pull
      // the "<key>" out of the href and find whichever rendered id ends
      // with "__<key>" (or matches the bare "shopify-section-<key>" form).
      const key = targetId.includes('__')
        ? targetId.split('__').pop()
        : targetId.replace(/^shopify-section-/, '');

      if (key) {
        target = document.querySelector(
          `[id$="__${key}"], [id="shopify-section-${key}"]`
        );
      }
    }

    if (!target) return;

    event.preventDefault();
    // Win the race against whatever else is listening for clicks on these
    // anchors (see root cause #3 above) before it can act on this event.
    event.stopImmediatePropagation();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Keep the URL fragment in sync without triggering a second, redundant
    // native jump (history.pushState alone does not scroll).
    if (history.pushState) {
      history.pushState(null, '', hash);
    }
  },
  true // capture: run ahead of any bubble-phase listener that might hijack the click.
);
